import { createContext, PropsWithChildren, useState } from "react";
import { TealiumLinkData } from "../common/analytics";
import { DateObject } from "../common/objects/models/date.field.value";

export interface CreateOrUnlockAccountState {
    certificationChecked: boolean
    emailAddress: string
    confirmEmailAddress: string
    coverageStartDate: DateObject
    dateOfBirth: DateObject
    lastName: string
    suffix: string
    lastNameWithSuffix: string
    medicareNumber: string
    noEmailAddressChecked: boolean
    part: 'A' | 'B',
    analyticsPart: TealiumLinkData['coverage_type'],
    secretQuestion: string
    secretAnswer: string
    zipCodeOrCity: string
}

/** Default state for both create account and unlock account */
export const getDefaultCreateOrUnlockAccountState = (): CreateOrUnlockAccountState => ({
    certificationChecked: false,
    emailAddress: '',
    confirmEmailAddress: '',
    coverageStartDate: { day: '', month: '', year: '' },
    dateOfBirth: { day: '', month: '', year: '' },
    lastName: '',
    suffix: '',
    lastNameWithSuffix: '',
    medicareNumber: '',
    noEmailAddressChecked: false,
    part: 'A',
    analyticsPart: 'Part A',
    secretQuestion: '',
    secretAnswer: '',
    zipCodeOrCity: ''
});

export interface CreateOrUnlockAccountData {
    data: CreateOrUnlockAccountState
    updateData: (newData: Partial<CreateOrUnlockAccountState>) => void
    stepForward: () => void
    stepBackward: () => void
}

export const createOrUnlockAccountData = createContext<CreateOrUnlockAccountData>({
    data: getDefaultCreateOrUnlockAccountState(),
    updateData: () => {},
    stepForward: () => {},
    stepBackward: () => {}
});

interface CreateOrUnlockAccountDataProviderProps {
    value: CreateOrUnlockAccountData
}
export const CreateOrUnlockAccountDataProvider = (props: PropsWithChildren<CreateOrUnlockAccountDataProviderProps>) => (
    <createOrUnlockAccountData.Provider value={props.value}>
        { props.children }
    </createOrUnlockAccountData.Provider>
);

export const useCreateOrUnlockAccountData = () => {
    const [state, setState] = useState(getDefaultCreateOrUnlockAccountState());
    const updateState = (newState: Partial<CreateOrUnlockAccountState>) => {
        setState((prevState) => {
            return {
                ...prevState,
                ...newState,
                get lastNameWithSuffix() { return `${this.lastName} ${this.suffix}`.trim(); }
            };
        });
    }

    return {
        data: state,
        updateData: updateState
    }
}